import { useState, useEffect, type SyntheticEvent } from "react";
import { useSelector } from "react-redux";
import { isValid, addMinutes } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { toast } from "react-toastify";

import { POST_STATES } from "constants/PostsView";
import { FIELDS } from "constants/PostFormFields";
import { DATE_FORMAT } from "constants/common";
import DateTimePicker from "components/DateTimePicker/DateTimePicker";
import { Z_INDEX_POPOVER } from "components/ui/z-index";
import { getUser } from "selectors/commonSelectors";
import { getAccountsById } from "selectors/skedCoreSelectors";

import { isDateInThePast } from "utils/dates";
import { safeParseDateString } from "utils/date-helpers";
import { QuestionIconWithTooltip } from "ui";
import { ExternalLink } from "ui/Link";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { getScheduleDisplay } from "./post-form-actions.utils";
import { Post } from "../../../../features/post";
import { getQueueDisabledAccountsMessage } from "../../../../utils/queue";

const POST_NOW = `${POST_STATES.SCHEDULED}-NOW`;

const stopQueueHelpLinkPropagation = (event: SyntheticEvent) => {
  event.stopPropagation();
};

type PostStateOption = {
  value: string;
  title: JSX.Element;
  label: JSX.Element;
  ariaDisabled?: boolean;
};

const PostFormActions = ({
  post,
  samePostState,
  timePreference,
  onChange,
  disabled = false,
  canQueuePosts = true,
  isSummary,
  postState,
  inBulkTab,
}: {
  post: Post;
  samePostState: boolean;
  postState: string;
  timePreference: string;
  onChange: (changes: Partial<Post>) => void;
  disabled?: boolean;
  canQueuePosts?: boolean;
  isSummary?: boolean;
  inBulkTab?: boolean;
}) => {
  const user = useSelector(getUser);
  const accountsById = useSelector(getAccountsById);
  const timezone = user.timezone;
  const selected =
    postState === POST_STATES.SCHEDULED && post.isNow ? POST_NOW : postState;
  const [scheduledFor, setScheduledFor] = useState(getDefaultScheduledFor);
  const [inThePast, setInThePast] = useState(false);

  function onDateChange(newDateTime: string) {
    setScheduledFor(newDateTime);
    setDatePickerVisible(false);
    setInThePast(isDateInThePast(newDateTime, timezone));
    onChange && onChange({ [FIELDS.WHEN]: newDateTime });
  }

  function getDefaultScheduledFor() {
    const whenDate = post.when
      ? safeParseDateString(post.when, timezone)
      : null;
    if (post.when && whenDate && !isValid(whenDate)) {
      throw new Error(
        `The selected date of ${post.when} is not valid. Please try again or contact support if the issue persists.`
      );
    }

    const scheduledDate = whenDate ? whenDate : addMinutes(new Date(), 5);
    return formatInTimeZone(scheduledDate, timezone, DATE_FORMAT);
  }

  useEffect(() => {
    if (selected === POST_STATES.SCHEDULED) {
      const time = getDefaultScheduledFor();
      setScheduledFor(time);
      if (!post.when) {
        onChange({ [FIELDS.WHEN]: time });
      }
      setInThePast(isDateInThePast(time, timezone));
    }
  }, [selected]);

  const queueTooltip = (
    <div>
      Use your recurring posting schedule. Click for more (in a new window).
    </div>
  );
  const postAccounts =
    post.accounts?.length > 0
      ? post.accounts
      : (post.accountIds?.length ? post.accountIds : [post.accountId])
          .filter(Boolean)
          .map((accountId) => accountsById[accountId as string])
          .filter(Boolean);
  const queueDisabledAccounts = postAccounts.filter(
    (account) => !account.queueEnabled
  );
  const queueDisabledMessage =
    queueDisabledAccounts.length > 0
      ? getQueueDisabledAccountsMessage(queueDisabledAccounts)
      : "Set up Queue for all your selected accounts to post on a recurring schedule.";
  const disabledQueueTooltip = (
    <div>{queueDisabledMessage} Click for more (in a new window).</div>
  );

  const config: PostStateOption[] = [
    {
      value: POST_STATES.DRAFT,
      title: (
        <span className="flex items-center gap-2 font-inter text-sm">
          <i className="fa-regular fa-file" aria-hidden="true" />
          Draft
        </span>
      ),
      label: (
        <span
          className={cn(
            "flex items-center gap-2 font-inter text-sm",
            inBulkTab ? "font-medium" : "font-light"
          )}
        >
          <i className="fa-regular fa-file" aria-hidden="true" />
          Draft
        </span>
      ),
    },
    {
      value: POST_STATES.QUEUED,
      title: (
        <span
          className={cn(
            "flex items-center gap-2 font-inter text-sm",
            !canQueuePosts && "text-gray-600 cursor-not-allowed"
          )}
        >
          <i className="fa-solid fa-layer-group" aria-hidden="true" />
          Queue post&nbsp;
          <ExternalLink
            href={`https://help.skedsocial.com/en/articles/3811779-use-the-queue-function`}
            aria-label="Learn more about Queue posts"
            onPointerDown={stopQueueHelpLinkPropagation}
            onPointerUp={stopQueueHelpLinkPropagation}
            onClick={stopQueueHelpLinkPropagation}
          >
            <QuestionIconWithTooltip
              tooltip={canQueuePosts ? queueTooltip : disabledQueueTooltip}
            />
          </ExternalLink>
        </span>
      ),
      label: (
        <span
          className={cn(
            "flex items-center gap-2 font-inter text-sm",
            inBulkTab ? "font-medium" : "font-light",
            !canQueuePosts && "text-gray-600"
          )}
        >
          <i className="fa-solid fa-layer-group" aria-hidden="true" />
          Queue post
        </span>
      ),
      // Deliberately keep the item interactive so keyboard/mouse selection
      // can surface the explanatory toast and help link instead of silently
      // blocking the option.
      ariaDisabled: !canQueuePosts,
    },
    {
      value: POST_STATES.SCHEDULED,
      title: (
        <span className="flex items-center gap-2 font-inter text-sm">
          <i className="fa-regular fa-clock" aria-hidden="true" />
          Schedule for
        </span>
      ),
      label: (
        <span
          className={cn(
            "flex items-center gap-2 font-inter text-sm",
            inBulkTab ? "font-medium" : "font-light"
          )}
        >
          <i className="fa-regular fa-clock" aria-hidden="true" />
          Schedule for
        </span>
      ),
    },
    {
      value: POST_NOW,
      title: (
        <span className="flex items-center gap-2 font-inter text-sm">
          <i className="fa-regular fa-paper-plane" aria-hidden="true" />
          Post Now
        </span>
      ),
      label: (
        <span
          className={cn(
            "flex items-center gap-2 font-inter text-sm",
            inBulkTab ? "font-medium" : "font-light"
          )}
        >
          <i className="fa-regular fa-paper-plane" aria-hidden="true" />
          Post Now
        </span>
      ),
    },
  ];
  // add an archived option here for when user uses 'post again'
  if (selected === POST_STATES.ARCHIVED) {
    config.push({
      value: POST_STATES.ARCHIVED,
      title: (
        <span className="flex items-center gap-2 font-inter text-sm">
          Archived - choose one...
        </span>
      ),
      label: (
        <span className="flex items-center gap-2 font-inter text-sm font-light">
          Archived - choose one...
        </span>
      ),
      ariaDisabled: true,
    });
  }

  const handlePostStateChange = (value: string) => {
    if (value === POST_STATES.QUEUED && !canQueuePosts) {
      toast.error(queueDisabledMessage);
      return;
    }

    const nextPostState =
      value === POST_NOW
        ? POST_STATES.SCHEDULED
        : (value as (typeof POST_STATES)[keyof typeof POST_STATES]);

    onChange &&
      onChange({
        [FIELDS.POST_STATE]: nextPostState,
        [FIELDS.IS_NOW]: value === POST_NOW,
      } as Partial<Post>);
  };
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  const scheduledClasses = "bg-blue-37 text-black";
  const scheduledErrorClasses = "bg-red-100 text-red-500";

  return (
    <div
      className="flex items-center gap-2 whitespace-nowrap"
      data-testid="postCreateTypeSelectWrapper"
    >
      {!isSummary && (
        <Select value={selected} onValueChange={handlePostStateChange}>
          <SelectTrigger
            className={cn(
              "text-sm",
              inBulkTab
                ? "h-8 w-[200px] min-w-[200px] rounded-lg px-3 py-1.5 font-medium"
                : "min-w-[190px] font-light"
            )}
          >
            <SelectValue placeholder="Select one">
              {config.find((c) => c.value === selected)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {config.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                aria-disabled={option.ariaDisabled}
              >
                {option.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {selected === POST_STATES.ARCHIVED && (
        <DisplayBox className="text-black bg-red-50" compact={inBulkTab}>
          Select action for this post.
        </DisplayBox>
      )}
      {selected === POST_STATES.DRAFT && (
        <DisplayBox className="text-black bg-blue-37" compact={inBulkTab}>
          {samePostState
            ? "This post is a draft"
            : "This post will be sent to drafts"}
        </DisplayBox>
      )}
      {selected === POST_STATES.QUEUED && (
        <DisplayBox className="text-black bg-blue-37" compact={inBulkTab}>
          {samePostState
            ? "This post is queued"
            : "This post will be sent to the queue"}
        </DisplayBox>
      )}
      {selected === POST_STATES.SCHEDULED && !isSummary && (
        <DisplayBox
          compact={inBulkTab}
          className={`relative text-black ${
            inThePast ? scheduledErrorClasses : scheduledClasses
          }`}
        >
          {inBulkTab ? "" : "Scheduled for:"}
          <span
            className={`underline cursor-pointer ml-4 ${
              inThePast ? "text-red-400" : "text-blue-500"
            }`}
            onClick={() =>
              !disabled && setDatePickerVisible(!datePickerVisible)
            }
          >
            {getScheduleDisplay(scheduledFor, timePreference, timezone)}
          </span>
          {datePickerVisible && (
            <div
              className="absolute right-0"
              style={{ bottom: "100%", zIndex: Z_INDEX_POPOVER }}
            >
              <DateTimePicker
                selectedDate={scheduledFor}
                onChange={onDateChange}
                timePreference={timePreference}
                onClose={() => setDatePickerVisible(false)}
                maxSelectableDate={undefined}
              />
            </div>
          )}
        </DisplayBox>
      )}
      {selected === `${POST_STATES.SCHEDULED}-NOW` && (
        <DisplayBox className="bg-blue-200 text-black" compact={inBulkTab}>
          Scheduled to post ASAP
        </DisplayBox>
      )}
      {selected === POST_STATES.SCHEDULED && isSummary && (
        <div className="h-9 bg-green-50 text-green-600 text-sm font-medium flex items-center px-3 rounded-lg">
          Posted: {getScheduleDisplay(scheduledFor, timePreference, timezone)}
        </div>
      )}
    </div>
  );
};

const DisplayBox = ({
  children,
  className = "",
  compact,
}: {
  children: React.ReactNode;
  className?: string;
  compact?: boolean;
}) => {
  return (
    <div
      className={cn(
        "flex items-center flex-wrap rounded-lg font-inter text-sm font-semibold",
        compact
          ? "h-8 w-fit min-w-[178px] shrink-0 justify-center px-3 leading-5"
          : "h-9 px-3",
        className
      )}
    >
      {children}
    </div>
  );
};
export default PostFormActions;
